<template>
  <div class="tw-min-h-screen tw-bg-slate-50">
    <div class="tw-mx-auto tw-w-full tw-max-w-7xl tw-px-4 tw-py-6 sm:tw-px-6 lg:tw-px-8">
      <div class="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-start sm:tw-justify-between">
        <div class="tw-min-w-0">
          <div class="tw-flex tw-items-center tw-gap-4">
            <button
              type="button"
              class="tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50"
              aria-label="Back to orders"
              @click="goBack"
            >
              <font-awesome-icon icon="arrow-left" class="tw-h-4 tw-w-4" />
            </button>
            <div class="tw-pl-1">
              <h1 class="tw-text-left tw-text-lg tw-font-semibold tw-text-slate-900">New Order</h1>
              <p class="tw-mt-1 tw-text-left tw-text-sm tw-text-slate-600">Create an order and add containers/loads.</p>
            </div>
          </div>
        </div>

        <div class="tw-flex tw-items-center tw-gap-2">
          <button
            type="button"
            class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
            @click="goBack"
          >
            Cancel
          </button>
          <button
            type="button"
            class="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800"
            :disabled="loading"
            @click="addNewOrder"
          >
            <font-awesome-icon
              v-if="loading"
              icon="circle-notch"
              spin
              class="tw-mr-2 tw-h-4 tw-w-4"
            />
            {{ orderCreationButtonText }}
          </button>
        </div>
      </div>

      <div
        v-if="submissionError"
        class="tw-mt-4 tw-rounded-xl tw-border tw-border-rose-200 tw-bg-rose-50 tw-px-4 tw-py-3 tw-text-sm tw-text-rose-900"
      >
        {{ submissionError }}
      </div>

      <div
        v-if="createdOrder"
        class="tw-mt-6 tw-rounded-2xl tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-p-5"
      >
        <div class="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
          <div>
            <div class="tw-text-sm tw-font-semibold tw-text-emerald-900">Created Order #{{ createdOrder.id }}</div>
            <div class="tw-mt-1 tw-text-sm tw-text-emerald-900/90">
              <span class="tw-font-medium">Drop-Off:</span> {{ createdOrder.receiver?.name || '—' }}
            </div>
            <div class="tw-text-sm tw-text-emerald-900/90">
              <span class="tw-font-medium">Bill To:</span> {{ createdOrder.billTo?.name || '—' }}
            </div>
          </div>

          <div class="tw-flex tw-items-center tw-gap-2">
            <router-link
              class="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-emerald-700 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-emerald-800"
              :to="`/orders/${createdOrder.id}`"
            >
              View Order
            </router-link>
            <button
              type="button"
              class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-emerald-300 tw-bg-white/60 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-emerald-900 hover:tw-bg-white"
              @click="resetAfterCreate"
            >
              Create Another
            </button>
          </div>
        </div>

        <div class="tw-mt-4 tw-overflow-x-auto">
          <table class="tw-min-w-full tw-text-left tw-text-sm">
            <thead class="tw-text-xs tw-uppercase tw-tracking-wide tw-text-emerald-900/70">
              <tr>
                <th class="tw-px-3 tw-py-2">Container</th>
                <th class="tw-px-3 tw-py-2">Size/Type</th>
                <th class="tw-px-3 tw-py-2">Terminal</th>
              </tr>
            </thead>
            <tbody class="tw-divide-y tw-divide-emerald-200/60">
              <tr v-for="(c, idx) in createdOrder.containers || []" :key="`created-container-${idx}`">
                <td class="tw-px-3 tw-py-2">{{ c.containerId && c.containerId !== 'EXPUNDEF' ? c.containerId : 'Container undefined' }}</td>
                <td class="tw-px-3 tw-py-2">{{ c.sizeType }}</td>
                <td class="tw-px-3 tw-py-2">{{ c.terminal }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="tw-mt-6 tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-900/5">
        <div class="tw-border-b tw-border-slate-200/70 tw-px-5 tw-py-4">
          <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
            <div class="tw-flex tw-items-center tw-gap-2">
              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                :class="isOceanOrder ? 'tw-border-transparent tw-bg-slate-900 tw-text-white' : 'tw-border-slate-200 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50'"
                @click="setOrderType(ORDER_TYPE.OCEAN_IMPORT)"
              >
                <font-awesome-icon icon="ship" class="tw-h-4 tw-w-4" />
                <span>Ocean</span>
              </button>
              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                :class="isRailOrder ? 'tw-border-transparent tw-bg-slate-900 tw-text-white' : 'tw-border-slate-200 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50'"
                @click="setOrderType(ORDER_TYPE.RAIL_PICK_UP)"
              >
                <font-awesome-icon icon="train" class="tw-h-4 tw-w-4" />
                <span>Rail</span>
              </button>
              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                :class="isLandOrder ? 'tw-border-transparent tw-bg-slate-900 tw-text-white' : 'tw-border-slate-200 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50'"
                @click="setOrderType(ORDER_TYPE.LAND)"
              >
                <font-awesome-icon icon="truck" class="tw-h-4 tw-w-4" />
                <span>Land</span>
              </button>
            </div>

            <div v-if="isOceanOrder" class="tw-flex tw-items-center tw-gap-2">
              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                :class="isImportOrder ? 'tw-border-transparent tw-bg-indigo-600 tw-text-white' : 'tw-border-slate-200 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50'"
                @click="setOrderType(ORDER_TYPE.OCEAN_IMPORT)"
              >
                <font-awesome-icon icon="arrow-down" class="tw-mr-2 tw-h-4 tw-w-4" />
                Import
              </button>
              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                :class="isOceanExport ? 'tw-border-transparent tw-bg-indigo-600 tw-text-white' : 'tw-border-slate-200 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50'"
                @click="setOrderType(ORDER_TYPE.OCEAN_EXPORT)"
              >
                <font-awesome-icon icon="arrow-up" class="tw-mr-2 tw-h-4 tw-w-4" />
                Export
              </button>
            </div>

            <div v-if="isRailOrder" class="tw-flex tw-items-center tw-gap-2">
              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                :class="isRailPickUp ? 'tw-border-transparent tw-bg-indigo-600 tw-text-white' : 'tw-border-slate-200 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50'"
                @click="setOrderType(ORDER_TYPE.RAIL_PICK_UP)"
              >
                <font-awesome-icon icon="arrow-left" class="tw-mr-2 tw-h-4 tw-w-4" />
                Rail Pick-Up
              </button>
              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                :class="isRailDropOff ? 'tw-border-transparent tw-bg-indigo-600 tw-text-white' : 'tw-border-slate-200 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50'"
                @click="setOrderType(ORDER_TYPE.RAIL_DELIVERY)"
              >
                <font-awesome-icon icon="arrow-right" class="tw-mr-2 tw-h-4 tw-w-4" />
                Rail Delivery
              </button>
            </div>
          </div>
        </div>

        <div class="tw-p-5">
          <NewOrderForm
            @onBillingCustomerSearch="onBillingCustomerSearch"
            @onReceivingCustomerSearch="onReceivingCustomerSearch"
            @onBLSearch="onBLSearch"
            :billingCustomers="billingCustomers || []"
            :receivingCustomers="receivingCustomers || []"
            :activeBLs="activeBLs || []"
            :blStatus="blStatus"
            :terminals="terminals || []"
            :orderData="orderData"
            :orderType="orderData.orderType"
            :fieldErrors="fieldErrors"
            :settingsOrderShowLinkedOrderNumberField="!!(company && company.settingsOrderShowLinkedOrderNumberField)"
            :showVesselInformation="!!(company && company.settingsOrderShowVesselInformation)"
            :settingsOrderShowEmergencyInformation="!!(company && company.settingsOrderShowEmergencyInformation)"
            :settingsOrderLtlEnabled="!!(company && company.settingsOrderLtlEnabled)"
            :showBrokerFields="!!(company && company.isBroker)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import ActiveBLs from '@/gql/orders/ActiveBLs.gql';
import BLStatusQuery from '@/gql/orders/BLStatus.gql';
import TerminalsQuery from '@/gql/terminals/Terminals.gql';
import AddDeliveryOrder from '@/gql/orders/mutations/AddDeliveryOrder.gql';

import { SteamShipLinesMap } from '../../utils/constants';
import { showGraphQLError } from '../../utils/data_extraction';
import { pushToast } from '@/lib/toast';
import CompanyQuery from '../../gql/company/Company.gql';
import BillingCustomersQuery from '../../gql/customers/BillingCustomers.gql';
import ReceivingCustomersQuery from '../../gql/customers/ReceivingCustomers.gql';
import CustomerQuery from '../../gql/customers/Customer.gql';
import ContainerQuery from '@/gql/containers/Container.gql';

import isContainer from '../../utils/container_validator';
import NewOrderForm from './NewOrders/NewOrderForm.vue';

import { addressMixin } from '../../utils/address';
import { dateFormatMixin } from '../../utils/date';
import { orderTypeMixin } from '../../utils/order';

import { ORDER_TYPE } from '../../types';

import {
  addOrderDataShipline,
  addTerminalToOrderData,
  preparePayloadForMutation,
} from './NewOrders/orderData';
import {
  activeBlPrefillKey,
  blExistsInActiveBls,
  findActiveBl,
} from '@/utils/active_BLs_apollo';

export default defineComponent({
  name: 'NewOrderPage',
  mixins: [addressMixin, dateFormatMixin, orderTypeMixin],
  components: {
    NewOrderForm,
  },
  data() {
    return {
      createdOrder: undefined as any,
      company: null as any,
      activeBls: [] as any[],
      blStatus: null as any,
      terminals: [] as any[],
      billingCustomers: [] as any[],
      receivingCustomers: [] as any[],
      billingCustomerNameQuery: '',
      receivingCustomerNameQuery: '',
      blNameQuery: '',
      shouldLoadBLs: false,
      isExistingBL: false,
      routePrefillApplied: false,
      lastBLPrefillKey: null as string | null,
      containerPrefillApplied: false,
      loading: false,
      ORDER_TYPE,
      submissionError: '',
      fieldErrors: {} as Record<string, string>,
      orderData: {
        orderType: ORDER_TYPE.OCEAN_IMPORT,
        isDropAndHook: false,
        billOfLadingNumber: undefined,
        bookingNumber: undefined,
        customerReference: undefined,
        billingCustomerReference: undefined,
        customerReferences: [''],
        billingCustomerReferences: [''],
        linkedMasterOrderNumber: undefined,
        pickUpLocation: undefined,
        dropOffLocation: undefined,
        dropOffContact: {
          name: undefined,
          phoneNumber: undefined,
          email: undefined,
        },
        billTo: undefined,
        billToContact: {
          name: undefined,
          phoneNumber: undefined,
          email: undefined,
        },
        shipLineInfo: {
          line: undefined,
          vessel: undefined,
          eta: undefined,
        },
        brokerSailingInfo: {
          portOfLoading: undefined,
          portOfDischarge: undefined,
          placeOfReceipt: undefined,
          placeOfDelivery: undefined,
          vesselVoyage: undefined,
          sailingDate: undefined,
          arrivalDate: undefined,
        },
        containers: [] as any[],
        carrierName: undefined,
        scacCode: undefined,
        emergencyResponsePhoneNumber: undefined,
        houseBillOfLading: undefined,
      } as any,
    };
  },
  computed: {
    orderType(): any {
      return (this as any).orderData?.orderType;
    },
    orderCreationButtonText(): string {
      return 'Create Order';
    },
    doesBLExist(): boolean {
      const vm = (this as any);
      return blExistsInActiveBls(vm.activeBLs, vm.orderData.billOfLadingNumber);
    },
    mappedBLs(): any[] {
      const vm = (this as any);
      if (!vm.activeBLs) return [];
      return vm.activeBLs.map((x) => `${x.billOfLadingId}`);
    },
    isImportOrder(): boolean {
      return (this as any).orderData.orderType === ORDER_TYPE.OCEAN_IMPORT;
    },
    containerNumbersValid(): boolean {
      const vm = (this as any);
      if (vm.orderData.containers.length > 0) {
        return vm.orderData.containers.reduce((acc, x) => {
          if (!acc) return false;
          const containerId = String(x.containerId || '').trim();
          if (!containerId) return !vm.isImportOrder;
          if (!isContainer(containerId)) return false;
          return true;
        }, true);
      }
      return true;
    },
    orderFormValid(): boolean {
      const vm = (this as any);
      if (vm.orderData) {
        if (!vm.orderData.pickUpLocation) return false;
        if (!vm.orderData.dropOffLocation?.name) return false;
        if (!vm.orderData.billTo?.name) return false;
        return true;
      }
      return true;
    },
    activeBLs(): any[] {
      const vm = (this as any);
      if (!vm.activeBls) return [];
      return vm.activeBls;
    },
  },
  watch: {
    '$route.query': {
      handler(query: Record<string, unknown>) {
        const vm = this as any;
        vm.applyRoutePrefill();
        const blFromRoute = String(query?.billOfLadingNumber || '').trim();
        if (blFromRoute) {
          vm.shouldLoadBLs = true;
          vm.$nextTick(() => {
            const activeBlsQuery = vm.$apollo?.queries?.activeBls;
            if (activeBlsQuery && !activeBlsQuery.skip) {
              activeBlsQuery.refetch();
            }
          });
        }
      },
      immediate: true,
      deep: true,
    },
    activeBLs: {
      handler() {
        (this as any).applyRoutePrefill();
      },
      immediate: true,
    },
    'orderData.billOfLadingNumber': {
      handler(val: string | undefined) {
        const vm = this as any;
        const normalized = String(val || '').trim();
        if (normalized.length >= 3) {
          vm.shouldLoadBLs = true;
        }
      },
      immediate: true,
    },
  },
  methods: {
    goBack() {
      const vm = (this as any);
      vm.$router.push('/orders');
    },
    resetAfterCreate() {
      const vm = (this as any);
      vm.createdOrder = undefined;
      vm.resetFormData();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    setOrderType(orderType: any) {
      const vm = this as any;
      vm.orderData.orderType = orderType;

      // Clear mutually exclusive reference fields when switching order type
      if (orderType === ORDER_TYPE.OCEAN_IMPORT) {
        vm.orderData.bookingNumber = undefined;
      }
      if (orderType === ORDER_TYPE.OCEAN_EXPORT) {
        vm.orderData.billOfLadingNumber = undefined;
      }

      // Land orders use freight contents (loads); ensure a baseline container exists
      if (orderType === ORDER_TYPE.LAND && (!vm.orderData.containers || vm.orderData.containers.length === 0)) {
        vm.orderData.containers = [
          {
            isAdded: true,
            freightContents: [],
          },
        ];
      }
    },
    applyRoutePrefill() {
      const vm = this as any;

      const query = vm.$route?.query || {};
      const prefillBLNumber = String(query.billOfLadingNumber || '').trim();
      const hasDropoffPrefill = !!(query.dropoff || query.customerId || query.id);

      if (!prefillBLNumber && !hasDropoffPrefill) {
        return;
      }

      if (prefillBLNumber) {
        vm.orderData.billOfLadingNumber = prefillBLNumber;
        vm.shouldLoadBLs = true;

        const activeBl = findActiveBl(vm.activeBLs, prefillBLNumber);
        if (activeBl) {
          const prefillKey = activeBlPrefillKey(prefillBLNumber, activeBl);
          if (vm.lastBLPrefillKey !== prefillKey) {
            vm.selectBL(activeBl.billOfLadingId);
            vm.lastBLPrefillKey = prefillKey;
          }
        }
        return;
      }

      if (hasDropoffPrefill && !vm.routePrefillApplied) {
        vm.routePrefillApplied = true;
      }
    },
    applyContainerPrefill(container: any) {
      const vm = this as any;
      if (!container || vm.containerPrefillApplied) return;

      if (!vm.orderData.dropOffLocation && container.customer) {
        vm.orderData.dropOffLocation = container.customer;
      }

      if (!vm.orderData.shipLineInfo.line && container.owner && container.owner !== 'N/A') {
        vm.orderData.shipLineInfo.line = container.owner;
      }
      if (!vm.orderData.shipLineInfo.vessel && container.vessel?.name) {
        vm.orderData.shipLineInfo.vessel = container.vessel.name;
      }
      if (!vm.orderData.shipLineInfo.eta && container.eta && container.eta !== 'N/A') {
        vm.orderData.shipLineInfo.eta = container.eta;
      }

      if ((!vm.orderData.containers || vm.orderData.containers.length === 0) && container.containerId) {
        const sizeType = String(container.sizeType || '');
        const size = sizeType.length >= 2 ? sizeType.substring(0, 2) : undefined;
        const type = sizeType.length > 2 ? sizeType.substring(2) : undefined;
        vm.orderData.containers = [
          {
            containerId: container.containerId,
            size,
            type,
            sealNumber: undefined,
            isAdded: true,
            freightContents: [
              {
                quantity: undefined,
                packingType: undefined,
                isHazmat: false,
                weight: undefined,
                description: undefined,
                nmfcNumber: undefined,
                nmfcClass: undefined,
              },
            ],
          },
        ];
      }

      vm.containerPrefillApplied = true;
    },
    selectBL(val: any) {
      const vm = (this as any);
      const activeBl = findActiveBl(vm.activeBLs, val);
      if (activeBl) {
        const containers = activeBl.containers.slice();
        (vm.orderData as any).containers = containers.map((x) => {
          const { id, ...dat } = x;
          dat.freightContents = dat.freightContents.map((y) => {
            return {
              quantity: y.cargoPieces,
              packingType: y.packingType,
              isHazmat: y.isHazmat,
              weight: y.weight,
              description: y.cargoDescription,
              nmfcNumber: y.nmfcNumber,
              nmfcClass: y.nmfcClass,
            };
          });
          return dat;
        });

        if (vm.terminals) {
          addTerminalToOrderData(activeBl.pickUpLocation, vm.terminals, vm.orderData);
        }
        if (activeBl.latestVessel) {
          vm.orderData.shipLineInfo.vessel = activeBl.latestVessel.name;
        }
        addOrderDataShipline(val, vm.orderData);
      }
    },
    validateFields(): Record<string, string> {
      const vm = (this as any);
      const errors: Record<string, string> = {};

      if (vm.isImportOrder) {
        if (!vm.orderData.pickUpLocation) {
          errors.pickUpLocation = 'Specify pick-up location';
        }
        if (!vm.orderData.dropOffLocation?.name) {
          errors.dropOffLocation = 'Specify drop-off location';
        }
        if (!vm.orderData.billTo?.name) {
          errors.billTo = 'Specify bill-to';
        }
        if (!vm.validateContainerNumberValid()) {
          errors.containers = 'Your container number is not valid. Please check it.';
        }
      } else if (vm.orderData.orderType === ORDER_TYPE.OCEAN_EXPORT) {
        if (!vm.orderData.bookingNumber) {
          errors.bookingNumber = 'Specify Booking Number';
        }
      }

      return errors;
    },
    validateContainerNumberValid(): boolean {
      const vm = (this as any);
      if (vm.orderData.containers.length > 0) {
        return vm.orderData.containers.reduce((acc, x) => {
          if (!acc) return false;
          const containerId = String(x.containerId || '').trim();
          if (!containerId) return !vm.isImportOrder;
          if (!isContainer(containerId)) return false;
          return true;
        }, true);
      }
      return true;
    },
    resetFormData() {
      const vm = (this as any);
      vm.orderData = {
        orderType: ORDER_TYPE.OCEAN_IMPORT,
        isDropAndHook: false,
        billOfLadingNumber: undefined,
        bookingNumber: undefined,
        customerReference: undefined,
        billingCustomerReference: undefined,
        customerReferences: [''],
        billingCustomerReferences: [''],
        linkedMasterOrderNumber: undefined,
        pickUpLocation: undefined,
        dropOffLocation: undefined,
        dropOffContact: {
          name: undefined,
          phoneNumber: undefined,
          email: undefined,
        },
        brokerSailingInfo: {
          portOfLoading: undefined,
          portOfDischarge: undefined,
          placeOfReceipt: undefined,
          placeOfDelivery: undefined,
          vesselVoyage: undefined,
          sailingDate: undefined,
          arrivalDate: undefined,
        },
        billTo: undefined,
        billToContact: {
          name: undefined,
          phoneNumber: undefined,
          email: undefined,
        },
        shipLineInfo: {
          line: undefined,
          vessel: undefined,
          eta: '',
        },
        containers: [],
        carrierName: undefined,
        scacCode: undefined,
        emergencyResponsePhoneNumber: undefined,
        houseBillOfLading: undefined,
      };
    },
    onBillingCustomerSearch(customerName: string) {
      (this as any).billingCustomerNameQuery = customerName;
    },
    onReceivingCustomerSearch(customerName: string) {
      (this as any).receivingCustomerNameQuery = customerName;
    },
    onBLSearch(val: string) {
      const vm = this as any;
      vm.blNameQuery = val;
      if (String(val || '').trim().length >= 3) {
        vm.shouldLoadBLs = true;
      }
    },
    addNewOrder() {
      const vm = (this as any);

      vm.loading = true;
      vm.submissionError = '';
      vm.fieldErrors = {};
      const errors = vm.validateFields();
      if (Object.keys(errors).length > 0) {
        vm.fieldErrors = errors;
        const containerError = errors.containers;
        if (containerError) {
          pushToast(containerError, 'error');
        }
        vm.loading = false;
        return;
      }

      const payload = preparePayloadForMutation(vm.orderData, vm.isImportOrder);

      vm.$apollo
        .mutate({
          mutation: AddDeliveryOrder,
          variables: payload,
        })
        .then((result) => {
          vm.loading = false;
          const addDeliveryOrderResult = result.data.addDeliveryOrder;
          const order = addDeliveryOrderResult.order;
          const emailSent = !!addDeliveryOrderResult.emailSent;
          const emailStatus = String(addDeliveryOrderResult.emailStatus || '').trim();
          vm.resetFormData();
          vm.createdOrder = order;
          vm.submissionError = '';
          vm.fieldErrors = {};
          if (emailStatus) {
            pushToast(emailStatus, emailSent ? 'success' : 'warning');
          } else {
            pushToast('Order created.', 'success');
          }
        })
        .catch((err) => {
          console.error(err);
          vm.loading = false;
          vm.submissionError = showGraphQLError(err);
        });
    },
  },
  apollo: {
    company: {
      query: CompanyQuery,
      update: (data: any) => data.company,
      fetchPolicy: 'cache-first',
    },
    activeBls: {
      query: ActiveBLs,
      update: (data: any) => data.activeBls,
      fetchPolicy: 'cache-and-network',
      skip() {
        const vm = this as any;
        return !vm.shouldLoadBLs;
      },
      result() {
        (this as any).applyRoutePrefill();
      },
    },
    blStatus: {
      query: BLStatusQuery,
      variables() {
        return {
          billOfLadingNumber: String((this as any).orderData?.billOfLadingNumber || '').trim(),
        };
      },
      update: (data: any) => data.blStatus,
      fetchPolicy: 'cache-and-network',
      skip() {
        const vm = this as any;
        return !vm.isImportOrder || String(vm.orderData?.billOfLadingNumber || '').trim().length < 3;
      },
    },
    terminals: {
      query: TerminalsQuery,
      update: (data: any) => data.terminals,
      fetchPolicy: 'cache-first',
    },
    billingCustomers: {
      query: BillingCustomersQuery,
      debounce: 100,
      variables() {
        return {
          name: (this as any).billingCustomerNameQuery,
        };
      },
      update: (data: any) => data.billingCustomers,
      skip() {
        const vm = this as any;
        return String(vm.billingCustomerNameQuery || '').trim().length < 2;
      },
    },
    receivingCustomers: {
      query: ReceivingCustomersQuery,
      debounce: 100,
      variables() {
        return {
          name: (this as any).receivingCustomerNameQuery,
        };
      },
      update: (data: any) => data.receivingCustomers,
      skip() {
        const vm = this as any;
        return String(vm.receivingCustomerNameQuery || '').trim().length < 2;
      },
    },
    customer: {
      query: CustomerQuery,
      variables() {
        const vm = this as any;
        return {
          customerId: vm.$route?.query?.dropoff || vm.$route?.query?.customerId || vm.$route?.query?.id,
        };
      },
      skip() {
        const vm = this as any;
        return !vm.$route?.query?.dropoff && !vm.$route?.query?.customerId && !vm.$route?.query?.id;
      },
      result({ data }: any) {
        const vm = this as any;
        if (data?.customer) {
          vm.orderData.dropOffLocation = data.customer;
        }
      },
    },
    prefillContainer: {
      query: ContainerQuery,
      variables() {
        return {
          id: (this as any).$route?.query?.containerId,
        };
      },
      skip() {
        const vm = this as any;
        return !vm.$route?.query?.containerId;
      },
      result({ data }: any) {
        const vm = this as any;
        if (data?.container) {
          vm.applyContainerPrefill(data.container);
        }
      },
    },
  },
});
</script>
